import { ReactNode, useMemo } from 'react';
import useApp from '@/hooks/use-app.hook';
import { HomeContext } from './helpers';
import { PublicServiceMeta } from '@/lib/models/public-service';
import useAppData from '@/hooks/use-app-data.hook';
import { PolicyModel } from '@/lib/models/policy.model';
import useUserSettings from '@/hooks/use-user-settings.hook';
import { compact, orderBy, uniqBy } from 'lodash';
import { getRecentServicesByUser } from '@/lib/services/analytics.service';
import { useQuery } from 'react-query';
import { QueryKey } from '@/lib/query-client';
import { getTimestamp } from '@/lib/date-helpers';
import { TileMetaModel, TileModel } from '@/lib/models/user-settings/tile.model';
import { getAllThreads } from '@/lib/services/thread.service';
import { ORG_DEFAULT_AGENT_ID } from '@/lib/services/shortcut.service';

export const HomeProvider = ({ children }: { children: ReactNode | ReactNode[] }) => {
  const { signedIn } = useApp();
  const { home: homeSettings, agents } = useUserSettings();

  const { data: threads = [], isLoading: isLoadingThreads } = useQuery(
    [QueryKey.ThreadsList],
    async () => getAllThreads()
  );

  const { data: recentByAnalytics = [], isLoading: isLoadingRecentServices } = useQuery(
    QueryKey.UserRecentServices,
    () =>
      getRecentServicesByUser().then((recentServices) =>
        recentServices.map(({ serviceId: resourceId, mostRecentVisitTimestamp }) => {
          return new TileMetaModel({
            createdAt: getTimestamp(mostRecentVisitTimestamp),
            tileType: 'public-service',
            resourceId,
          });
        })
      ),
    {
      enabled: signedIn,
    }
  );

  const { data: userPolicy, isLoading: isLoadingUserPolicy } = useAppData<PolicyModel>(
    'portal-policy',
    {
      enabled: signedIn,
    }
  );

  const availableServices = useMemo(
    () => userPolicy?.allAvailablePublicServices || [],
    [userPolicy]
  );

  const { data: publicServices = [], isLoading: isLoadingPublicServices } = useAppData<
    PublicServiceMeta[]
  >('public-service-catalog', {
    enabled: signedIn,
  });

  const isLoading =
    isLoadingRecentServices || isLoadingUserPolicy || isLoadingPublicServices || isLoadingThreads;

  const [tiles, favorites, recent, publicServiceTiles]: [
    TileModel[],
    TileModel[],
    TileModel[],
    TileModel[],
  ] = useMemo(() => {
    if (isLoading) {
      return [[], [], [], []];
    }

    const allTiles: TileModel[] = [];

    // @todo sort by timestamp and then deduplicate
    const allRecent = uniqBy(
      orderBy([...recentByAnalytics, ...homeSettings.recent], 'createdAt', 'desc'),
      'tileId'
    );

    const getRecentTile = (tileId: string): TileMetaModel | undefined => {
      return allRecent.find(({ tileId: recentTileId }) => recentTileId === tileId);
    };

    const isFavorite = (tileId: string): boolean | undefined => {
      return !!homeSettings.favorites.find(
        ({ tileId: favoriteTileId }) => favoriteTileId === tileId
      );
    };

    publicServices.forEach((publicService) => {
      const { id: resourceId, name, website } = publicService;

      const hasAccess = availableServices.includes(publicService.id);

      const tile = new TileModel({
        tileType: 'public-service',
        resourceId,
        hasAccess,
      });

      tile.keywords = [name, website];

      allTiles.push(tile);
    });

    agents.forEach((agent) => {
      const {
        id: resourceId,
        portal: {
          badge: { title },
        },
      } = agent;
      const tile = new TileModel({ tileType: 'agent', resourceId });

      tile.keywords = [title];

      allTiles.push(tile);
    });

    threads.forEach((thread) => {
      const { id: resourceId, latestUserMessage } = thread;

      const { agentId = ORG_DEFAULT_AGENT_ID } = latestUserMessage || {};

      const tile = new TileModel({
        tileType: 'conversation',
        resourceId,
        serviceId: agentId,
      });

      allTiles.push(tile);
    });

    const favoriteMap: Map<string, TileModel> = new Map();
    const unsortedRecent: TileModel[] = [];

    // All tiles must know if they are favorited or recent, so appropriate
    // icons, actions can be shown when they are rendered
    const allTilesDecorated = allTiles.map((tile) => {
      tile.recent = getRecentTile(tile.tileId)?.createdAt;
      tile.favorite = isFavorite(tile.tileId);

      if (tile.favorite) {
        favoriteMap.set(tile.tileId, new TileModel({ ...tile._props }));
      }

      if (tile.recent) {
        unsortedRecent.push(new TileModel({ ...tile._props }));
      }

      return tile;
    });

    // preserve the ordering in the users settings and be sure to remove any
    // removed tiles, or services where the user may have lost access due to policy changes
    const favorites: TileModel[] = compact(
      homeSettings.favorites.map(({ tileId }) => favoriteMap.get(tileId))
    );

    const recent = orderBy(unsortedRecent, 'recent', 'desc');
    const availableTiles = allTilesDecorated.filter(({ hasAccess }) => hasAccess);
    const publicServiceTiles = allTilesDecorated.filter(
      ({ tileType }) => tileType === 'public-service'
    );

    return [availableTiles, favorites, recent, publicServiceTiles];
  }, [
    isLoading,
    availableServices,
    recentByAnalytics,
    publicServices,
    homeSettings,
    agents,
    threads,
  ]);

  return (
    <HomeContext.Provider
      value={{
        publicServices,
        publicServiceTiles,
        tiles,
        favorites,
        hasFavorites: !!favorites.length,
        recent,
        hasRecent: !!recent.length,
        hasAgents: !!agents.length,
        hasServices: !!availableServices.length,
        isLoading,
      }}
    >
      {children}
    </HomeContext.Provider>
  );
};
